// apps/web/src/features/users/components/UserEditDrawer.tsx
// Spec §14.4 / plan Task 5.4. Two-stage PATCH with email-change confirm:
//   1. Diff form values against the server baseline via `pickChangedFields`.
//   2. If the diff is empty, short-circuit.
//   3. If `email` is in the diff, open <ConfirmModal danger> and wait for
//      explicit confirm before calling mutate.
//   4. Otherwise mutate directly.
//
// Issue #628 PR1 (now #680): the drawer is also the reference implementation
// for the shared unsaved-changes guard. Drawer Escape/backdrop, DetailSheet
// back button, browser back, and dirty tab switches all route through the
// EditorDrawer wrapper guard so unsaved profile edits cannot be lost
// silently. Dirty uses `pickChangedFields` semantics (not raw
// `formState.isDirty`) so reverting a typed change to baseline marks the
// form clean.
//
// The drawer uses the <Drawer> chrome (no `open` prop — caller unmounts) and
// the <DetailSheet> body (header + tabs + form|actions). Labels use
// <label htmlFor>/<input id> so RTL can drive inputs via getByLabelText.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  PatchUserInputSchema,
  type PatchUserInput,
  type User,
  type Department,
} from '@magimp/shared';
import { useUser, usePatchUser, useDepartmentsList, useRolesList } from '@magimp/shared-hooks';
import {
  EditorDrawer,
  DetailSheet,
  ConfirmModal,
  Button,
  StatusBadge,
  Avatar,
  LoadingState,
  ErrorState,
  Select,
  PhoneInput,
  Tabs,
  useEditorGuard,
  type SelectOption,
} from '../../../ui/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import { pickChangedFields } from '../../../shared/pickChangedFields.js';
import { UserDeactivateConfirm } from './UserDeactivateConfirm.js';
import { UserReactivateConfirm } from './UserReactivateConfirm.js';
import { UserResetPasswordModal } from './UserResetPasswordModal.js';
import { phoneError } from '../../../shared/index.js';

const DIFF_KEYS: (keyof PatchUserInput)[] = ['email', 'name', 'phone', 'departmentId', 'roleId'];

export interface UserEditDrawerProps {
  id: string;
  onClose: () => void;
}

export function UserEditDrawer({ id, onClose }: UserEditDrawerProps) {
  const { t } = useTranslation('common');
  const userQ = useUser(id);
  // Dirty/busy lifted out of `ProfileForm` so the drawer can wire one
  // guard that covers Drawer/DetailSheet/Cancel paths and dirty tab
  // switches. The form reports up via narrow primitive callbacks.
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <EditorDrawer width={560} dirty={dirty} busy={busy} onConfirmClose={onClose}>
      {userQ.isPending && <LoadingState />}
      {userQ.error && <ErrorState title={t('states.loadError')} text={errorMessage(userQ.error)} />}
      {userQ.isSuccess && (
        <LoadedDrawer
          user={userQ.data}
          onClose={onClose}
          onDirtyChange={setDirty}
          onBusyChange={setBusy}
        />
      )}
    </EditorDrawer>
  );
}

interface LoadedDrawerProps {
  user: User;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}

function LoadedDrawer({ user, onClose, onDirtyChange, onBusyChange }: LoadedDrawerProps) {
  const { t } = useTranslation(['users', 'common']);
  // WHY: `confirmDestructive` is needed for the dirty tab-switch guard
  // (profile ↔ actions). Tabs unmount the active form, dropping its local
  // RHF state; routing the switch through the editor guard surfaces the
  // discard prompt so typed-but-unsaved input is not silently lost.
  const { confirmDestructive } = useEditorGuard();
  const [tab, setTab] = useState<'profile' | 'actions'>('profile');
  const requestTab = (next: 'profile' | 'actions') => {
    if (next === tab) return;
    confirmDestructive(() => setTab(next));
  };
  return (
    <DetailSheet
      title={user.name}
      onClose={() => confirmDestructive(onClose)}
      right={
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Avatar name={user.name} />
          <StatusBadge status={user.active ? 'active' : 'inactive'} />
          {user.passwordResetRequired && <StatusBadge status="passwordResetRequired" size="sm" />}
        </div>
      }
    >
      <Tabs<'profile' | 'actions'>
        tabs={[
          { id: 'profile', label: t('users:tabs.profile') },
          { id: 'actions', label: t('users:tabs.actions') },
        ]}
        value={tab}
        onChange={requestTab}
      />
      {tab === 'profile' ? (
        <ProfileForm user={user} onDirtyChange={onDirtyChange} onBusyChange={onBusyChange} />
      ) : (
        <ActionsTab user={user} />
      )}
    </DetailSheet>
  );
}

function ProfileForm({
  user,
  onDirtyChange,
  onBusyChange,
}: {
  user: User;
  onDirtyChange: (dirty: boolean) => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation(['users', 'common', 'departments']);
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { errors },
    reset,
  } = useForm<PatchUserInput>({
    resolver: zodResolver(PatchUserInputSchema),
    defaultValues: {
      name: user.name,
      phone: user.phone,
      departmentId: user.departmentId,
      roleId: user.role.id,
      email: user.email,
    },
  });
  useEffect(() => {
    reset({
      name: user.name,
      phone: user.phone,
      departmentId: user.departmentId,
      roleId: user.role.id,
      email: user.email,
    });
  }, [user.id, user.email, user.name, user.phone, user.departmentId, user.role.id, reset]);

  // Dirty bubble-up: compute via the same `pickChangedFields` semantics the
  // submit path uses, so a reverted-to-baseline edit closes without warning.
  // Subscribe via `watch(callback)` so we only react to field changes, not
  // to every parent re-render.
  const baseline = useMemo<Record<string, unknown>>(
    () => ({ ...user, roleId: user.role.id }),
    [user],
  );
  const computeDirty = useCallback(
    (values: Partial<PatchUserInput>): boolean => {
      const diff = pickChangedFields(
        values as unknown as Record<string, unknown>,
        baseline,
        DIFF_KEYS as (keyof Record<string, unknown>)[],
      );
      return Object.keys(diff).length > 0;
    },
    [baseline],
  );
  useEffect(() => {
    // Seed parent state with the current value at mount / after baseline reset.
    onDirtyChange(computeDirty(getValues()));
    const subscription = watch((values) => {
      onDirtyChange(computeDirty(values));
    });
    return () => subscription.unsubscribe();
  }, [watch, computeDirty, getValues, onDirtyChange]);
  // Reset dirty when this form unmounts (tab switch to actions) so the
  // parent guard does not keep a stale-dirty signal for an already-discarded
  // local form state.
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  // Active departments populate the dropdown; if the edited user is pinned to
  // an archived department, that row is rendered at the top with a localized
  // suffix so the admin sees the current state and can change it. The server
  // blocks only new or changed assignments to archived departments.
  const activeQ = useDepartmentsList({ active: true });
  const allQ = useDepartmentsList({});
  const allById = new Map<string, Department>((allQ.data ?? []).map((d) => [d.id, d]));
  const currentDept = allById.get(user.departmentId);
  const departmentOptions: SelectOption[] = (() => {
    const active = (activeQ.data ?? [])
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
      .map((d) => ({ value: d.id, label: d.name }));
    if (currentDept && currentDept.archivedAt !== null) {
      // Render the archived current selection at the top so it remains
      // selectable in the form value but is visually marked.
      return [
        {
          value: currentDept.id,
          label: `${currentDept.name} (${t('departments:detail.archivedSuffix')})`,
        },
        ...active,
      ];
    }
    return active;
  })();

  const rolesQ = useRolesList();
  const roleOptions: SelectOption[] = (rolesQ.data ?? []).map((r) => ({
    value: r.id,
    label: r.displayName,
  }));

  const patch = usePatchUser(user.id);
  const show = useToastStore((s) => s.show);
  const [pendingEmailConfirm, setPendingEmailConfirm] = useState<PatchUserInput | null>(null);

  // Bubble pending-save state up to the drawer guard so close attempts
  // (Esc/backdrop/Cancel/back) are suppressed while a PATCH is in flight.
  useEffect(() => {
    onBusyChange(patch.isPending);
  }, [patch.isPending, onBusyChange]);
  useEffect(() => () => onBusyChange(false), [onBusyChange]);

  const computeDiff = (): PatchUserInput => {
    const values = getValues() as unknown as Record<string, unknown>;
    // INVARIANT: baseline must expose `roleId` (not the embedded RoleRef) so
    // pickChangedFields can compare apples-to-apples with the form value.
    const baseline: Record<string, unknown> = { ...user, roleId: user.role.id };
    return pickChangedFields(
      values,
      baseline,
      DIFF_KEYS as (keyof Record<string, unknown>)[],
    ) as PatchUserInput;
  };

  const submitPatch = (body: PatchUserInput) => {
    patch.mutate(body, {
      onSuccess: () => show({ tone: 'success', text: t('users:toasts.saved') }),
      onError: (err) => show({ tone: 'error', text: errorMessage(err) }),
    });
  };

  const onSubmit = () => {
    const diff = computeDiff();
    if (Object.keys(diff).length === 0) return;
    if ('email' in diff) {
      setPendingEmailConfirm(diff);
      return;
    }
    submitPatch(diff);
  };
  const onEmailConfirm = () => {
    if (!pendingEmailConfirm) return;
    submitPatch(pendingEmailConfirm);
    setPendingEmailConfirm(null);
  };
  const onEmailClose = () => setPendingEmailConfirm(null);

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit(onSubmit)(e);
        }}
        noValidate
      >
        <LabeledInput id="email" label="Email" error={errors.email?.message}>
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <input
                id="email"
                type="email"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                style={inputStyle(!!errors.email)}
              />
            )}
          />
        </LabeledInput>
        <LabeledInput id="name" label={t('users:fields.name')} error={errors.name?.message}>
          <Controller
            control={control}
            name="name"
            render={({ field }) => (
              <input
                id="name"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                style={inputStyle(!!errors.name)}
              />
            )}
          />
        </LabeledInput>
        <LabeledInput
          id="phone"
          label={t('users:fields.phone')}
          error={phoneError(errors.phone?.message, t('users:errors.invalidPhone'))}
        >
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneInput
                id="phone"
                value={field.value ?? ''}
                onChange={field.onChange}
                invalid={!!errors.phone}
                testId="user-edit-phone"
              />
            )}
          />
        </LabeledInput>
        <LabeledInput
          id="departmentId"
          label={t('users:fields.department')}
          error={errors.departmentId?.message}
        >
          <Controller
            control={control}
            name="departmentId"
            render={({ field }) => (
              <Select
                value={field.value ?? ''}
                onChange={(v) => field.onChange(v)}
                options={departmentOptions}
                placeholder={
                  activeQ.isPending
                    ? t('users:placeholders.departmentLoading')
                    : t('users:placeholders.departmentSelect')
                }
              />
            )}
          />
        </LabeledInput>
        <LabeledInput id="roleId" label={t('users:fields.role')} error={errors.roleId?.message}>
          <Controller
            control={control}
            name="roleId"
            render={({ field }) => (
              <Select
                value={field.value ?? user.role.id}
                onChange={(v) => field.onChange(v)}
                options={roleOptions}
                placeholder={
                  rolesQ.isPending
                    ? t('users:placeholders.roleLoading')
                    : t('users:placeholders.roleSelect')
                }
              />
            )}
          />
        </LabeledInput>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <Button type="submit" kind="primary" disabled={patch.isPending}>
            {t('common:actions.save')}
          </Button>
        </div>
      </form>
      {pendingEmailConfirm && (
        <ConfirmModal
          title={t('users:confirm.emailChangeTitle')}
          text={t('users:confirm.emailChangeText')}
          confirmLabel={t('users:confirm.emailChangeConfirm')}
          cancelLabel={t('common:actions.cancel')}
          danger
          onConfirm={onEmailConfirm}
          onClose={onEmailClose}
        />
      )}
    </>
  );
}

function LabeledInput({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
      <label htmlFor={id} style={{ fontSize: 12, color: 'var(--fg-2)' }}>
        {label}
      </label>
      {children}
      {error && (
        <span role="alert" style={{ fontSize: 11, color: 'var(--status-error-fg)' }}>
          {error}
        </span>
      )}
    </div>
  );
}

function inputStyle(invalid: boolean): React.CSSProperties {
  return {
    height: 'var(--control-h)',
    padding: '0 10px',
    borderRadius: 'var(--r-2)',
    border: `1px solid ${invalid ? 'var(--status-error-fg)' : 'var(--border-1)'}`,
    background: 'var(--bg-1)',
    color: 'var(--fg-1)',
    fontFamily: 'var(--font-ui)',
    fontSize: 13,
  };
}

function ActionsTab({ user }: { user: User }) {
  const { t } = useTranslation('common');
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const isSelf = viewerId !== null && viewerId === user.id;
  const [deactivate, setDeactivate] = useState(false);
  const [reactivate, setReactivate] = useState(false);
  const [reset, setReset] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {user.active ? (
        <Button kind="danger" disabled={isSelf} onClick={() => setDeactivate(true)}>
          {t('actions.deactivate')}
        </Button>
      ) : (
        <Button kind="primary" disabled={isSelf} onClick={() => setReactivate(true)}>
          {t('actions.reactivate')}
        </Button>
      )}
      <Button kind="ghost" disabled={isSelf} onClick={() => setReset(true)}>
        {t('actions.resetPassword')}
      </Button>
      {deactivate && <UserDeactivateConfirm user={user} onClose={() => setDeactivate(false)} />}
      {reactivate && <UserReactivateConfirm user={user} onClose={() => setReactivate(false)} />}
      {reset && <UserResetPasswordModal user={user} onClose={() => setReset(false)} />}
    </div>
  );
}
